import { useState, useEffect } from 'react';

/**
 * Desktop-first responsive breakpoints
 * Priority: Desktop > Tablet > Mobile
 */
export const BREAKPOINTS = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1023px)', 
  desktop: '(min-width: 1024px)',
  large: '(min-width: 1440px)'
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Hook for responsive media queries
 * @param query - CSS media query string
 * @returns boolean indicating if the query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);
  
  useEffect(() => {
    const media = window.matchMedia(query);
    
    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    
    // Set initial state
    setMatches(media.matches);
    
    // Add listener with fallback for older browsers
    if (media.addEventListener) {
      media.addEventListener('change', listener);
    } else {
      // Fallback for older browsers
      media.addListener(listener);
    }
    
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', listener);
      } else {
        media.removeListener(listener);
      }
    };
  }, [query]);
  
  return matches;
};

/**
 * Hook to get current breakpoint information
 * @returns object with breakpoint flags and current breakpoint
 */
export const useBreakpoint = () => {
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const isTablet = useMediaQuery(BREAKPOINTS.tablet);
  const isDesktop = useMediaQuery(BREAKPOINTS.desktop);
  const isLarge = useMediaQuery(BREAKPOINTS.large);
  
  // Determine current breakpoint with desktop-first priority
  const current: BreakpointKey = isLarge ? 'large' : 
                                 isDesktop ? 'desktop' : 
                                 isTablet ? 'tablet' : 'mobile';
  
  return {
    isMobile,
    isTablet, 
    isDesktop,
    isLarge,
    current,
    // Convenience flags
    isDesktopOrLarger: isDesktop || isLarge,
    isTabletOrLarger: isTablet || isDesktop || isLarge,
    isMobileOnly: isMobile && !isTablet && !isDesktop,
  };
};

/**
 * Hook for responsive values based on breakpoints
 * @param values - Object with values for each breakpoint
 * @returns current value based on active breakpoint
 */
export const useResponsiveValue = <T>(values: {
  mobile?: T;
  tablet?: T;
  desktop?: T;
  large?: T;
}) => {
  const { current } = useBreakpoint();
  
  // Desktop-first cascade: try current, then desktop, then tablet, then mobile
  return values[current] ?? 
         values.desktop ?? 
         values.tablet ?? 
         values.mobile;
}; 